import React, { forwardRef, useEffect, useState } from 'react';
import { DataGridPro, type DataGridProProps } from '@mui/x-data-grid-pro';
import { GridPagination } from '@mui/x-data-grid';
import LegacyAnimatedDataGridPro, { type DensityMode } from './AnimatedDataGridPro';

export type GridRendererMode = 'animated-component' | 'test-page';

const GRID_RENDERER_MODE_STORAGE_KEY = 'transporter.datagrid.rendererMode';
const GRID_RENDERER_MODE_EVENT = 'transporter.datagrid.rendererMode.changed';

const noAnimationSx = {
  '&.MuiDataGrid-root': {
    transition: 'none !important',
    animation: 'none !important',
  },
  '& .MuiDataGrid-main': {
    transition: 'none !important',
    animation: 'none !important',
  },
  '& .MuiDataGrid-virtualScroller': {
    transition: 'none !important',
    animation: 'none !important',
  },
  '& .MuiDataGrid-virtualScrollerContent': {
    transition: 'none !important',
    animation: 'none !important',
  },
  '& .MuiDataGrid-virtualScrollerRenderZone': {
    transition: 'none !important',
    animation: 'none !important',
  },
  '& .MuiDataGrid-row': {
    transition: 'none !important',
    animation: 'none !important',
  },
  '& .MuiDataGrid-cell': {
    transition: 'none !important',
    animation: 'none !important',
  },
  '& .MuiDataGrid-row.row--animate': {
    animation: 'none !important',
  },
};

const paginationLayerSx = {
  '& .MuiDataGrid-main': {
    position: 'relative',
    zIndex: 1,
  },
  '& .MuiDataGrid-footerContainer': {
    position: 'relative',
    zIndex: 3,
    backgroundColor: '#FFFFFF',
  },
};

const gridPaginationMenuZIndex = 2600;

const mergeSx = (existingSx: any, nextSx: any) => {
  if (!existingSx) {
    return nextSx;
  }

  if (Array.isArray(existingSx)) {
    return [nextSx, ...existingSx];
  }

  return [nextSx, existingSx];
};

const mergeGridSlotProps = (slotProps: any) => {
  const resolvedSlotProps = slotProps ?? {};
  const existingBaseSelect = resolvedSlotProps.baseSelect ?? {};
  const existingMenuProps = (existingBaseSelect as any).MenuProps ?? {};
  const existingMenuSlotProps = existingMenuProps.slotProps ?? {};
  const existingPagination = resolvedSlotProps.pagination ?? {};
  const existingPaginationSelectProps = existingPagination.SelectProps ?? {};
  const existingPaginationMenuProps =
    existingPaginationSelectProps.MenuProps ?? {};
  const existingPaginationMenuSlotProps =
    existingPaginationMenuProps.slotProps ?? {};
  const existingPaginationSlotProps = existingPagination.slotProps ?? {};
  const existingPaginationSelectSlotProps =
    existingPaginationSlotProps.select ?? {};
  const existingPaginationSelectSlotMenuProps =
    existingPaginationSelectSlotProps.MenuProps ?? {};
  const existingPaginationSelectSlotMenuSlotProps =
    existingPaginationSelectSlotMenuProps.slotProps ?? {};

  return {
    ...resolvedSlotProps,
    baseSelect: {
      ...existingBaseSelect,
      MenuProps: {
        ...existingMenuProps,
        disablePortal: false,
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'left',
        },
        transformOrigin: {
          vertical: 'bottom',
          horizontal: 'left',
        },
        sx: mergeSx(existingMenuProps.sx, {
          zIndex: gridPaginationMenuZIndex,
        }),
        PaperProps: {
          ...(existingMenuProps.PaperProps ?? {}),
          sx: mergeSx(existingMenuProps.PaperProps?.sx, {
            zIndex: gridPaginationMenuZIndex + 1,
          }),
        },
        slotProps: {
          ...existingMenuSlotProps,
          root: {
            ...(existingMenuSlotProps.root ?? {}),
            sx: mergeSx(existingMenuSlotProps.root?.sx, {
              zIndex: gridPaginationMenuZIndex,
            }),
          },
          paper: {
            ...(existingMenuSlotProps.paper ?? {}),
            sx: mergeSx(existingMenuSlotProps.paper?.sx, {
              zIndex: gridPaginationMenuZIndex + 1,
            }),
          },
        },
      },
    },
    pagination: {
      ...existingPagination,
      SelectProps: {
        ...existingPaginationSelectProps,
        MenuProps: {
          ...existingPaginationMenuProps,
          variant: 'menu',
          disablePortal: false,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'left',
          },
          transformOrigin: {
            vertical: 'bottom',
            horizontal: 'left',
          },
          sx: mergeSx(existingPaginationMenuProps.sx, {
            zIndex: gridPaginationMenuZIndex,
          }),
          PaperProps: {
            ...(existingPaginationMenuProps.PaperProps ?? {}),
            sx: mergeSx(existingPaginationMenuProps.PaperProps?.sx, {
              zIndex: gridPaginationMenuZIndex + 1,
            }),
          },
          slotProps: {
            ...existingPaginationMenuSlotProps,
            root: {
              ...(existingPaginationMenuSlotProps.root ?? {}),
              sx: mergeSx(existingPaginationMenuSlotProps.root?.sx, {
                zIndex: gridPaginationMenuZIndex,
              }),
            },
            paper: {
              ...(existingPaginationMenuSlotProps.paper ?? {}),
              sx: mergeSx(existingPaginationMenuSlotProps.paper?.sx, {
                zIndex: gridPaginationMenuZIndex + 1,
              }),
            },
          },
        },
      },
      slotProps: {
        ...existingPaginationSlotProps,
        select: {
          ...existingPaginationSelectSlotProps,
          MenuProps: {
            ...existingPaginationSelectSlotMenuProps,
            variant: 'menu',
            disablePortal: false,
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'left',
            },
            transformOrigin: {
              vertical: 'bottom',
              horizontal: 'left',
            },
            sx: mergeSx(existingPaginationSelectSlotMenuProps.sx, {
              zIndex: gridPaginationMenuZIndex,
            }),
            PaperProps: {
              ...(existingPaginationSelectSlotMenuProps.PaperProps ?? {}),
              sx: mergeSx(
                existingPaginationSelectSlotMenuProps.PaperProps?.sx,
                {
                  zIndex: gridPaginationMenuZIndex + 1,
                },
              ),
            },
            slotProps: {
              ...existingPaginationSelectSlotMenuSlotProps,
              root: {
                ...(existingPaginationSelectSlotMenuSlotProps.root ?? {}),
                sx: mergeSx(
                  existingPaginationSelectSlotMenuSlotProps.root?.sx,
                  {
                    zIndex: gridPaginationMenuZIndex,
                  },
                ),
              },
              paper: {
                ...(existingPaginationSelectSlotMenuSlotProps.paper ?? {}),
                sx: mergeSx(
                  existingPaginationSelectSlotMenuSlotProps.paper?.sx,
                  {
                    zIndex: gridPaginationMenuZIndex + 1,
                  },
                ),
              },
            },
          },
        },
      },
    },
  };
};

const getMergedPaginationMenuProps = (menuProps: any) => {
  const existingMenuSlotProps = menuProps?.slotProps ?? {};

  return {
    ...menuProps,
    variant: 'menu',
    disablePortal: false,
    anchorOrigin: {
      vertical: 'top',
      horizontal: 'left',
    },
    transformOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    sx: mergeSx(menuProps?.sx, {
      zIndex: gridPaginationMenuZIndex,
    }),
    PaperProps: {
      ...(menuProps?.PaperProps ?? {}),
      sx: mergeSx(menuProps?.PaperProps?.sx, {
        zIndex: gridPaginationMenuZIndex + 1,
      }),
    },
    slotProps: {
      ...existingMenuSlotProps,
      root: {
        ...(existingMenuSlotProps.root ?? {}),
        sx: mergeSx(existingMenuSlotProps.root?.sx, {
          zIndex: gridPaginationMenuZIndex,
        }),
      },
      paper: {
        ...(existingMenuSlotProps.paper ?? {}),
        sx: mergeSx(existingMenuSlotProps.paper?.sx, {
          zIndex: gridPaginationMenuZIndex + 1,
        }),
      },
    },
  };
};

const ModalGridPagination = forwardRef<any, any>(function ModalGridPagination(
  props,
  ref,
) {
  const existingSelectProps = props?.SelectProps ?? {};
  const existingSelectSlotProps = props?.slotProps ?? {};
  const existingSelectSlot = existingSelectSlotProps.select ?? {};

  return (
    <GridPagination
      ref={ref}
      {...props}
      SelectProps={{
        ...existingSelectProps,
        MenuProps: getMergedPaginationMenuProps(existingSelectProps.MenuProps),
      }}
      slotProps={{
        ...existingSelectSlotProps,
        select: {
          ...existingSelectSlot,
          MenuProps: getMergedPaginationMenuProps(existingSelectSlot.MenuProps),
        },
      }}
    />
  );
});

const parseGridRendererMode = (value: unknown): GridRendererMode | null => {
  if (value === 'animated-component' || value === 'test-page') {
    return value;
  }

  return null;
};

const getGridRendererModeFromQuery = (): GridRendererMode | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const queryMode = new URLSearchParams(window.location.search).get(
    'datagridRenderer',
  );

  return parseGridRendererMode(queryMode);
};

const getGridRendererModeFromStorage = (): GridRendererMode | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedMode = window.localStorage.getItem(
      GRID_RENDERER_MODE_STORAGE_KEY,
    );
    return parseGridRendererMode(storedMode);
  } catch (_error) {
    return null;
  }
};

export const getGridRendererMode = (): GridRendererMode => {
  return (
    getGridRendererModeFromQuery() ??
    getGridRendererModeFromStorage() ??
    'animated-component'
  );
};

export const setGridRendererMode = (mode: GridRendererMode) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.setItem(GRID_RENDERER_MODE_STORAGE_KEY, mode);
  } catch (_error) {
    // Ignore storage write failures and still notify listeners.
  }

  window.dispatchEvent(
    new CustomEvent(GRID_RENDERER_MODE_EVENT, {
      detail: { mode },
    }),
  );
};

interface TestAnimationDataGridProProps extends Omit<DataGridProProps, 'sx'> {
  density: DensityMode;
  animateRows?: boolean;
  rendererMode?: GridRendererMode;
  sx?: any;
  autoHeight?: boolean;
  expandRowOnClick?: boolean;
  pagination?: boolean;
  pageSize?: number;
  compactRowHeight?: number;
  standardRowHeight?: number;
}

const getMergedSx = (sx: any, modeSx: any) => {
  if (Array.isArray(sx)) {
    return [...sx, modeSx];
  }

  if (sx) {
    return [sx, modeSx];
  }

  return modeSx;
};

const TestAnimationDataGridPro = forwardRef<
  HTMLDivElement,
  TestAnimationDataGridProProps
>(
  (
    {
      density,
      animateRows = false,
      rendererMode,
      sx,
      slots,
      slotProps,
      autoHeight = true,
      expandRowOnClick = false,
      pagination = false,
      pageSize = 10,
      compactRowHeight = 32,
      standardRowHeight = 40,
      getRowClassName,
      rowHeight,
      ...otherProps
    },
    ref,
  ) => {
    const [resolvedRendererMode, setResolvedRendererMode] =
      useState<GridRendererMode>(() => rendererMode ?? getGridRendererMode());

    useEffect(() => {
      if (rendererMode) {
        setResolvedRendererMode(rendererMode);
        return;
      }

      const syncRendererMode = () => {
        setResolvedRendererMode(getGridRendererMode());
      };

      syncRendererMode();

      if (typeof window !== 'undefined') {
        window.addEventListener('storage', syncRendererMode);
        window.addEventListener(
          GRID_RENDERER_MODE_EVENT,
          syncRendererMode as EventListener,
        );
      }

      return () => {
        if (typeof window !== 'undefined') {
          window.removeEventListener('storage', syncRendererMode);
          window.removeEventListener(
            GRID_RENDERER_MODE_EVENT,
            syncRendererMode as EventListener,
          );
        }
      };
    }, [rendererMode]);

    const resolvedRowHeight =
      typeof rowHeight === 'number'
        ? rowHeight
        : density === 'compact'
          ? compactRowHeight
          : standardRowHeight;
    const mergedSlotProps = mergeGridSlotProps(slotProps);
    const mergedSlots = pagination
      ? {
          ...(slots ?? {}),
          pagination: ModalGridPagination,
        }
      : slots;

    if (resolvedRendererMode === 'animated-component') {
      const animatedModeSx = pagination
        ? getMergedSx(getMergedSx(sx, noAnimationSx), paginationLayerSx)
        : getMergedSx(sx, noAnimationSx);

      return (
        <LegacyAnimatedDataGridPro
          ref={ref}
          density={density}
          animateRows={false}
          sx={animatedModeSx}
          slots={mergedSlots}
          slotProps={mergedSlotProps}
          autoHeight={autoHeight}
          expandRowOnClick={expandRowOnClick}
          pagination={pagination}
          pageSize={pageSize}
          compactRowHeight={compactRowHeight}
          standardRowHeight={standardRowHeight}
          rowHeight={resolvedRowHeight}
          getRowClassName={getRowClassName}
          {...otherProps}
        />
      );
    }

    const enhancedGetRowClassName = (params: any) => {
      const classNames = [];

      if (animateRows) {
        classNames.push('row--animate');
      }

      if (getRowClassName) {
        const originalClassName = getRowClassName(params);
        if (originalClassName) {
          classNames.push(originalClassName);
        }
      }

      return classNames.join(' ');
    };

    const testRendererStyles = {
      '&.MuiDataGrid-root': {
        transition: 'none',
      },
      '& .MuiDataGrid-main': {
        transition: 'none',
      },
      '& .MuiDataGrid-virtualScroller': {
        transition: 'none',
      },
      '& .MuiDataGrid-virtualScrollerContent': {
        transition: 'none',
      },
      '& .MuiDataGrid-virtualScrollerRenderZone': {
        transition: 'none',
      },
      '& .MuiDataGrid-row': {
        transition: 'none',
      },
      '& .MuiDataGrid-cell': {
        transition: 'none',
        lineHeight: `${Math.max(resolvedRowHeight - 1, 1)}px`,
      },
      '& .MuiDataGrid-row.row--animate': {
        animation: 'none',
      },
      '& .MuiDataGrid-row[aria-expanded]': {
        ...(expandRowOnClick && {
          cursor: 'pointer',
          '& .MuiDataGrid-cell': {
            cursor: 'pointer',
          },
          '&:hover': {
            backgroundColor: 'rgba(0, 165, 255, 0.08)',
          },
        }),
      },
      '@keyframes rowFadeIn': {},
    };

    const mergedSx = pagination
      ? getMergedSx(
          getMergedSx(getMergedSx(sx, testRendererStyles), noAnimationSx),
          paginationLayerSx,
        )
      : getMergedSx(getMergedSx(sx, testRendererStyles), noAnimationSx);

    const paginationProps = pagination
      ? {
          pagination: true,
          pageSizeOptions: [5, 10, 25, 50, 100],
          initialState: {
            pagination: {
              paginationModel: {
                pageSize,
              },
            },
          },
          hideFooter: false,
        }
      : {
          hideFooter: otherProps.hideFooter !== false,
        };

    return (
      <DataGridPro
        ref={ref}
        density={density}
        rowHeight={resolvedRowHeight}
        autoHeight={autoHeight}
        getRowClassName={enhancedGetRowClassName}
        sx={mergedSx}
        slots={mergedSlots}
        slotProps={mergedSlotProps}
        {...paginationProps}
        {...otherProps}
      />
    );
  },
);

TestAnimationDataGridPro.displayName = 'TestAnimationDataGridPro';

export default TestAnimationDataGridPro;
